/**
 * Canonical Tool Part queries for Campaign Chat.
 * Single source of truth — do not duplicate findToolPart elsewhere.
 */

import {CAMPAIGN_WIDGET_TOOL_NAMES} from '../utils/campaignCompletionMessages';

function normalizeOptions(thirdArg) {
  if (thirdArg === null || thirdArg === undefined) {
    return {statuses: ['completed'], withMessage: false};
  }
  if (typeof thirdArg === 'string' || Array.isArray(thirdArg)) {
    return {statuses: thirdArg, withMessage: false};
  }
  if (typeof thirdArg === 'object') {
    return {
      statuses: thirdArg.statuses ?? ['completed'],
      withMessage: Boolean(thirdArg.withMessage),
      messageIndex: thirdArg.messageIndex ?? null,
    };
  }
  return {statuses: ['completed'], withMessage: false};
}

function normalizeStatusList(statuses) {
  if (statuses === null) {
    return null;
  }
  return Array.isArray(statuses) ? statuses : [statuses];
}

function partMatchesTool(part, toolName, statusList) {
  if (part.type !== `tool-${toolName}`) {
    return false;
  }
  if (part.state !== 'output-available') {
    return false;
  }

  const status = part.output?.status;
  if (!status) {
    return false;
  }

  if (statusList?.length) {
    return statusList.includes(status);
  }

  return true;
}

/**
 * @param {Array} messages
 * @param {string} toolName
 * @param {string|string[]|{ statuses?, withMessage?, messageIndex? }} [options]
 * @returns {object|null|{ part, message }}
 */
export function findToolPart(messages, toolName, options) {
  if (!Array.isArray(messages)) {
    return null;
  }

  const {statuses, withMessage, messageIndex} = normalizeOptions(options);
  const statusList = normalizeStatusList(statuses);

  const indexedMessages =
    messageIndex !== null && messageIndex >= 0
      ? [messages[messageIndex]].filter(Boolean)
      : messages;

  const matches = [];

  indexedMessages.forEach((msg) => {
    (msg.parts || []).forEach((part) => {
      if (partMatchesTool(part, toolName, statusList)) {
        matches.push({part, message: msg});
      }
    });
  });

  if (matches.length === 0) {
    return null;
  }

  const last = matches[matches.length - 1];
  return withMessage ? last : last.part;
}

/**
 * Chronological scan (newest message/part first) for awaiting interactions in catalog.
 */
export function findLatestAwaitingInteraction(
  messages,
  catalog,
  pendingSubmissions = {},
) {
  if (!Array.isArray(messages) || !Array.isArray(catalog)) {
    return null;
  }

  const toolNames = new Set(catalog.map((entry) => entry.toolName));

  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const msg = messages[messageIndex];
    if (msg?.role !== 'assistant') {
      continue;
    }
    const parts = msg.parts || [];
    for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = parts[partIndex];
      if (!part.type?.startsWith('tool-')) {
        continue;
      }
      const toolName = part.type.slice('tool-'.length);
      if (!toolNames.has(toolName)) {
        continue;
      }
      if (part.output?.status !== 'awaiting_selection') {
        continue;
      }
      const trackingId = part.toolCallId || part.output?.trackingId;
      if (trackingId && pendingSubmissions[trackingId]) {
        continue;
      }
      if (toolName && trackingId) {
        return {toolName, trackingId, part};
      }
    }
  }

  return null;
}

/**
 * Resolves the latest catalog interaction, applying pending as submitting on that
 * interaction only (does not fall through to older awaiting widgets).
 */
export function resolveLatestInteraction(
  messages,
  catalog,
  pendingSubmissions = {},
) {
  const latest = findLatestAwaitingInteraction(messages, catalog, {});
  if (!latest) {
    return null;
  }

  if (pendingSubmissions[latest.trackingId]) {
    return {
      toolName: latest.toolName,
      trackingId: latest.trackingId,
      status: 'submitting',
      part: latest.part,
    };
  }

  return {
    toolName: latest.toolName,
    trackingId: latest.trackingId,
    status: 'awaiting_selection',
    part: latest.part,
  };
}

export function findToolPartByTrackingId(messages, trackingId) {
  if (!trackingId || !Array.isArray(messages)) {
    return null;
  }

  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const parts = messages[messageIndex]?.parts || [];
    for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = parts[partIndex];
      if (!part?.type?.startsWith('tool-')) {
        continue;
      }
      if (matchesTrackingId(part, trackingId)) {
        return part;
      }
    }
  }

  return null;
}

export function findMessageIdByTrackingId(messages, trackingId) {
  if (!trackingId || !Array.isArray(messages)) {
    return null;
  }

  for (const message of messages) {
    for (const part of message.parts || []) {
      if (matchesTrackingId(part, trackingId)) {
        return message.id ?? null;
      }
    }
  }

  return null;
}

export function resolveActiveOperationIdentity(messages, snapshot = null, liveOverlay = null) {
  const part = getProcessingToolPart(messages);
  if (part) {
    return {
      trackingId: part.output?.trackingId || part.toolCallId || null,
      part,
      snapshotOp: null,
    };
  }

  const snapshotOp = snapshot?.workflow?.operation ?? null;
  if (snapshotOp?.trackingId && snapshotOp.status === 'processing') {
    return {
      trackingId: snapshotOp.trackingId,
      part: null,
      snapshotOp,
    };
  }

  const overlayOps = Object.values(liveOverlay?.operations ?? {}).filter(
    (op) => op.status === 'processing',
  );
  if (overlayOps.length === 1 && overlayOps[0]?.trackingId) {
    return {
      trackingId: overlayOps[0].trackingId,
      part: null,
      snapshotOp: null,
    };
  }

  return {
    trackingId: null,
    part: null,
    snapshotOp: null,
  };
}

export function findAwaitingToolPart(messages, toolNames) {
  if (!Array.isArray(toolNames)) {
    return null;
  }

  for (const toolName of toolNames) {
    const match = findToolPart(messages, toolName, {
      statuses: ['awaiting_selection'],
      withMessage: true,
    });
    if (match) {
      return match;
    }
  }

  return null;
}

function matchesTrackingId(part, trackingId) {
  return (
    part.toolCallId === trackingId || part.output?.trackingId === trackingId
  );
}

export function patchToolPartOutput(messages, toolName, trackingId, outputPatch) {
  if (!Array.isArray(messages) || !trackingId) {
    return messages;
  }

  return messages.map((msg) => ({
    ...msg,
    parts: (msg.parts || []).map((part) => {
      if (
        part.type === `tool-${toolName}` &&
        matchesTrackingId(part, trackingId)
      ) {
        return {
          ...part,
          state: 'output-available',
          output: {
            ...part.output,
            status: 'completed',
            ...outputPatch,
          },
        };
      }
      return part;
    }),
  }));
}

export function isInputLockedForTools(messages, toolNames) {
  return Boolean(findAwaitingToolPart(messages, toolNames));
}

const STRATEGY_TOOL_NAMES = [
  'gerar_documento_estrategia',
  'editar_documento_estrategia',
  'regerar_documento_estrategia',
];

const FULL_STRATEGY_PIPELINE_TOOL_NAMES = ['gerar_documento_estrategia'];

const STRATEGY_DOCUMENT_ONLY_TOOL_NAMES = [
  'editar_documento_estrategia',
  'regerar_documento_estrategia',
];

export function getStrategyToolNameFromPartType(partType) {
  if (!partType?.startsWith('tool-')) {
    return null;
  }
  return partType.slice('tool-'.length);
}

export function isFullStrategyPipelineToolType(partType) {
  const name = getStrategyToolNameFromPartType(partType);
  return Boolean(name && FULL_STRATEGY_PIPELINE_TOOL_NAMES.includes(name));
}

export function isStrategyDocumentOnlyToolType(partType) {
  const name = getStrategyToolNameFromPartType(partType);
  return Boolean(name && STRATEGY_DOCUMENT_ONLY_TOOL_NAMES.includes(name));
}

const ART_TOOL_NAMES = [
  'gerar_artes_campanha',
  'regerar_artes_campanha',
  'editar_arte_campanha',
  'editar_artes_lote',
];

const ART_QUEUE_TOOL_NAMES = [
  'gerar_artes_campanha',
  'regerar_artes_campanha',
  'editar_arte_campanha',
  'editar_artes_lote',
];

export function isStrategyToolPartType(partType) {
  if (!partType) {
    return false;
  }
  const name = partType.replace(/^tool-/, '');
  return STRATEGY_TOOL_NAMES.includes(name);
}

export function isArtToolPartType(partType) {
  if (!partType) {
    return false;
  }
  const name = partType.replace(/^tool-/, '');
  return ART_TOOL_NAMES.includes(name);
}

export function isArtQueueToolPartType(partType) {
  if (!partType) {
    return false;
  }
  const name = partType.replace(/^tool-/, '');
  return ART_QUEUE_TOOL_NAMES.includes(name);
}

export function findLatestArtQueueToolPart(messages) {
  if (!Array.isArray(messages)) {
    return null;
  }

  let latestPart = null;

  messages.forEach((msg) => {
    (msg.parts || []).forEach((part) => {
      if (
        isArtQueueToolPartType(part.type) &&
        part.state === 'output-available' &&
        ['processing', 'completed', 'error'].includes(part.output?.status)
      ) {
        latestPart = part;
      }
    });
  });

  return latestPart;
}

/**
 * Builds art queue step list from a persisted art generation Tool Part.
 */
export function buildArtQueueProgressSteps(part, options = {}) {
  const output = part?.output ?? {};
  const toolName = part?.type?.replace(/^tool-/, '');
  const toolStatus = output.status;
  const results = Array.isArray(output.results) ? output.results : [];
  const currentStep = Number(output.currentStep) || 0;
  const formatPieceLabel =
    options.formatPieceLabel ?? ((pieceNumber) => `Arte ${pieceNumber}`);

  if (toolName === 'editar_arte_campanha') {
    const pieceNumber = Number(output.pieceNumber) || 1;
    let status = 'pending';
    if (toolStatus === 'completed') {
      status = 'complete';
    } else if (toolStatus === 'error') {
      status = 'idle';
    }
    return [{text: formatPieceLabel(pieceNumber), status}];
  }

  if (toolName === 'editar_artes_lote') {
    let batchTotal = Number(output.totalSteps);
    if (!Number.isFinite(batchTotal) || batchTotal <= 0) {
      if (toolStatus === 'processing') {
        batchTotal = options.expectedTotal ?? (results.length > 0 ? results.length : 1);
      } else {
        batchTotal = results.length > 0 ? results.length : 1;
      }
    }

    if (toolStatus === 'processing') {
      return Array.from({length: batchTotal}, (_, index) => ({
        text: formatPieceLabel(index + 1),
        status: 'pending',
      }));
    }

    const completedThrough =
      toolStatus === 'completed'
        ? Math.max(currentStep, batchTotal)
        : currentStep;

    return Array.from({length: batchTotal}, (_, index) => {
      const pieceNumber = index + 1;
      if (toolStatus === 'completed') {
        if (pieceNumber <= completedThrough) {
          return {text: formatPieceLabel(pieceNumber), status: 'complete'};
        }
        return {text: formatPieceLabel(pieceNumber), status: 'idle'};
      }
      if (toolStatus === 'error') {
        return {text: formatPieceLabel(pieceNumber), status: 'idle'};
      }
      if (pieceNumber <= currentStep) {
        return {text: formatPieceLabel(pieceNumber), status: 'complete'};
      }
      if (pieceNumber === currentStep + 1) {
        return {text: formatPieceLabel(pieceNumber), status: 'pending'};
      }
      return {text: formatPieceLabel(pieceNumber), status: 'idle'};
    });
  }

  let totalSteps = Number(output.totalSteps);
  if (!Number.isFinite(totalSteps) || totalSteps <= 0) {
    totalSteps = options.expectedTotal ?? results.length;
  }

  if (!totalSteps) {
    return [];
  }

  return Array.from({length: totalSteps}, (_, index) => {
    const pieceNumber = index + 1;

    if (toolStatus === 'completed') {
      if (!results.length) {
        return {text: formatPieceLabel(pieceNumber), status: 'idle'};
      }
      if (pieceNumber <= currentStep) {
        return {text: formatPieceLabel(pieceNumber), status: 'complete'};
      }
      return {text: formatPieceLabel(pieceNumber), status: 'idle'};
    }

    if (toolStatus === 'processing') {
      if (pieceNumber <= currentStep) {
        return {text: formatPieceLabel(pieceNumber), status: 'complete'};
      }
      if (pieceNumber === currentStep + 1) {
        return {text: formatPieceLabel(pieceNumber), status: 'pending'};
      }
      return {text: formatPieceLabel(pieceNumber), status: 'idle'};
    }

    return {text: formatPieceLabel(pieceNumber), status: 'idle'};
  });
}

export function hasCompletedStrategyToolInMessages(messages) {
  if (!Array.isArray(messages)) {
    return false;
  }
  return STRATEGY_TOOL_NAMES.some((toolName) =>
    Boolean(findToolPart(messages, toolName, 'completed')),
  );
}

const ART_COMPLETION_TOOL_NAMES = [
  'gerar_artes_campanha',
  'regerar_artes_campanha',
  'editar_arte_campanha',
  'editar_artes_lote',
];

export function hasCompletedArtToolInMessages(messages) {
  if (!Array.isArray(messages)) {
    return false;
  }
  return ART_COMPLETION_TOOL_NAMES.some((toolName) =>
    Boolean(findToolPart(messages, toolName, 'completed')),
  );
}

export function getStrategyIntegrationMessageIndex(messages) {
  if (!Array.isArray(messages)) {
    return -1;
  }

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg?.role !== 'assistant') {
      continue;
    }

    const hasStrategyTool = msg.parts?.some(
      (part) =>
        isStrategyToolPartType(part.type) &&
        part.state === 'output-available' &&
        ['processing', 'completed', 'error'].includes(part.output?.status),
    );

    if (hasStrategyTool) {
      return i;
    }
  }

  return -1;
}

export function getStrategyMarkdownFromMessages(messages) {
  if (!Array.isArray(messages)) {
    return '';
  }

  const strategyTools = [
    'gerar_documento_estrategia',
    'regerar_documento_estrategia',
    'editar_documento_estrategia',
  ];

  let latestMarkdown = '';

  messages.forEach((msg) => {
    (msg.parts || []).forEach((part) => {
      const toolName = part.type?.startsWith('tool-')
        ? part.type.slice('tool-'.length)
        : null;
      if (!strategyTools.includes(toolName)) {
        return;
      }
      if (part.state !== 'output-available') {
        return;
      }
      const status = part.output?.status;
      if (status !== 'completed' && status !== 'awaiting_selection') {
        return;
      }
      const markdown = part.output?.strategyMarkdown;
      if (markdown) {
        latestMarkdown = markdown;
      }
    });
  });

  return latestMarkdown;
}

const ART_GENERATION_TOOL_NAMES = [
  'gerar_artes_campanha',
  'regerar_artes_campanha',
  'editar_artes_lote',
];

const ART_EDIT_TOOL_NAMES = ['editar_arte_campanha'];

function normalizeArtResultEntry(art, index = 0) {
  const pieceNumber = art.pieceNumber ?? art.piece_number ?? index + 1;
  const url = art.url ?? art.imageUrl ?? '';
  return {
    ...art,
    pieceNumber,
    piece_number: pieceNumber,
    url,
    imageUrl: art.imageUrl ?? url,
    legenda: art.legenda ?? art.legend ?? '',
    revision: art.revision ?? null,
  };
}

export function normalizeArtResultsList(results = []) {
  if (!Array.isArray(results)) {
    return [];
  }
  return results.map((art, index) => normalizeArtResultEntry(art, index));
}

function isRevisionNewer(nextRevision, existingRevision) {
  if (!nextRevision) {
    return false;
  }
  if (!existingRevision) {
    return true;
  }
  return String(nextRevision) > String(existingRevision);
}

/** Prefer overlay entry when it carries a newer revision or a fresher URL. */
function shouldPreferOverlayArt(existing, incoming) {
  if (!incoming.url && !incoming.imageUrl) {
    return false;
  }
  if (!existing?.url && !existing?.imageUrl) {
    return true;
  }

  if (isRevisionNewer(incoming.revision, existing.revision)) {
    return true;
  }
  if (isRevisionNewer(existing.revision, incoming.revision)) {
    return false;
  }

  const incomingUrl = incoming.url || incoming.imageUrl;
  const existingUrl = existing.url || existing.imageUrl;
  return Boolean(incomingUrl && incomingUrl !== existingUrl);
}

/** Overlay newer art URLs by pieceNumber (edits, socket, session medias). */
export function mergeArtResultsByPiece(base = [], overlays = []) {
  const byPiece = new Map();

  for (const art of base) {
    const normalized = normalizeArtResultEntry(art, byPiece.size);
    byPiece.set(normalized.pieceNumber, normalized);
  }

  for (const art of overlays) {
    const normalized = normalizeArtResultEntry(art, byPiece.size);
    if (!normalized.pieceNumber) {
      continue;
    }
    const existing = byPiece.get(normalized.pieceNumber);
    if (existing) {
      const preferOverlay = shouldPreferOverlayArt(existing, normalized);
      byPiece.set(normalized.pieceNumber, {
        ...existing,
        ...normalized,
        url: preferOverlay
          ? normalized.url || existing.url
          : existing.url || normalized.url,
        imageUrl: preferOverlay
          ? normalized.imageUrl || existing.imageUrl
          : existing.imageUrl || normalized.imageUrl,
        revision: preferOverlay
          ? normalized.revision ?? existing.revision
          : existing.revision ?? normalized.revision,
        legenda: normalized.legenda || existing.legenda,
      });
      continue;
    }
    byPiece.set(normalized.pieceNumber, normalized);
  }

  return Array.from(byPiece.values()).sort(
    (left, right) => left.pieceNumber - right.pieceNumber,
  );
}

export function getArtResultsFromMessages(messages) {
  if (!Array.isArray(messages)) {
    return [];
  }

  let results = [];
  const editTools = new Set(ART_EDIT_TOOL_NAMES);

  messages.forEach((msg) => {
    (msg.parts || []).forEach((part) => {
      const toolName = part.type?.startsWith('tool-')
        ? part.type.slice('tool-'.length)
        : null;
      if (!toolName) {
        return;
      }
      if (
        !ART_GENERATION_TOOL_NAMES.includes(toolName) &&
        !editTools.has(toolName)
      ) {
        return;
      }
      if (part.state !== 'output-available') {
        return;
      }

      const status = part.output?.status;
      const partResults = part.output?.results;
      const hasPartResults =
        Array.isArray(partResults) && partResults.length > 0;

      if (status === 'completed') {
        if (hasPartResults) {
          const normalized = normalizeArtResultsList(partResults);
          if (editTools.has(toolName)) {
            results = mergeArtResultsByPiece(results, normalized);
          } else {
            results = normalized;
          }
          return;
        }

        if (toolName === 'editar_arte_campanha' && part.output?.pieceNumber) {
          const patch = normalizeArtResultEntry({
            pieceNumber: part.output.pieceNumber,
            url: part.output.imageUrl ?? '',
            revision: part.output.revision ?? null,
          });
          if (patch.url) {
            results = mergeArtResultsByPiece(results, [patch]);
          }
        }
        return;
      }

      if (
        status === 'processing' &&
        ART_GENERATION_TOOL_NAMES.includes(toolName) &&
        hasPartResults
      ) {
        results = mergeArtResultsByPiece(
          results,
          normalizeArtResultsList(partResults),
        );
      }
    });
  });

  return results;
}

export function hasProcessingToolInMessages(messages) {
  if (!Array.isArray(messages)) {
    return false;
  }
  return messages.some(
    (msg) =>
      msg.role === 'assistant' &&
      msg.parts?.some(
        (part) =>
          part.type?.startsWith('tool-') &&
          part.output?.status === 'processing',
      ),
  );
}

export function getProcessingToolPart(messages) {
  if (!Array.isArray(messages)) {
    return null;
  }

  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const parts = messages[messageIndex]?.parts || [];
    for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = parts[partIndex];
      if (
        part.type?.startsWith('tool-') &&
        part.output?.status === 'processing'
      ) {
        return part;
      }
    }
  }

  return null;
}

export function isArtToolProcessing(messages) {
  const processingPart = getProcessingToolPart(messages);
  return Boolean(processingPart && isArtToolPartType(processingPart.type));
}

export function hasArtToolActivityInMessages(messages) {
  if (!Array.isArray(messages)) {
    return false;
  }
  return messages.some((msg) =>
    msg.parts?.some(
      (part) =>
        isArtToolPartType(part.type) &&
        part.state === 'output-available' &&
        ['processing', 'completed', 'error'].includes(part.output?.status),
    ),
  );
}

export function messagesFingerprint(messages) {
  if (!Array.isArray(messages)) {
    return '';
  }
  return JSON.stringify(
    messages.map((msg) => ({
      role: msg.role,
      parts: msg.parts?.map((part) => ({
        type: part.type,
        status: part.output?.status,
        toolCallId: part.toolCallId,
        currentStep: part.output?.currentStep,
        totalSteps: part.output?.totalSteps,
        stepName: part.output?.stepName,
        resultsLength: Array.isArray(part.output?.results)
          ? part.output.results.length
          : undefined,
      })),
    })),
  );
}

/**
 * Resolves post quantity from completed widget or session userInput.
 * @returns {number|null}
 */
export function getPostQuantityFromMessages(messages, sessionDetail = null) {
  const postQuantityPart =
    findToolPart(messages, CAMPAIGN_WIDGET_TOOL_NAMES.QUANTIDADE_ARTES, {
      statuses: ['completed'],
    }) ||
    (Array.isArray(sessionDetail?.messages)
      ? findToolPart(sessionDetail.messages, CAMPAIGN_WIDGET_TOOL_NAMES.QUANTIDADE_ARTES, {
        statuses: ['completed'],
      })
      : null);

  const fromWidget = postQuantityPart?.output?.selectedValue;
  if (typeof fromWidget === 'number' && fromWidget > 0) {
    return fromWidget;
  }

  const fromSession = sessionDetail?.userInput?.postQuantity;
  if (typeof fromSession === 'number' && fromSession > 0) {
    return fromSession;
  }

  return null;
}

/**
 * Expected art count for progress/preview UI — avoids hardcoded default of 5.
 * @returns {number|null}
 */
export function getExpectedArtCount(messages, sessionDetail = null) {
  const fromQuantity = getPostQuantityFromMessages(messages, sessionDetail);
  if (fromQuantity) {
    return fromQuantity;
  }

  const artResults = getArtResultsFromMessages(messages);
  if (artResults.length > 0) {
    return artResults.length;
  }

  const processingPart = getProcessingToolPart(messages);
  if (processingPart && isArtToolPartType(processingPart.type)) {
    const totalSteps = processingPart.output?.totalSteps;
    if (typeof totalSteps === 'number' && totalSteps > 0) {
      return totalSteps;
    }
  }

  return null;
}
